import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { Popover } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { useMe, useSession } from '../../auth/session';
import { cn } from '../../lib/cn';

// Org context switcher (§8 "Org context"): which tenant and org unit the user is working in.
// Requests carry it as X-Tenant-Id + X-Org-Context.

export function OrgSwitcher() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { org, membership, setOrg } = useSession();
  const memberships = me?.memberships ?? [];

  if (memberships.length === 0) {
    return (
      <span className="flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1.5 text-sm font-semibold text-text">
        <Building2 size={15} /> {t('shell.orgSwitcher.platform')}
      </span>
    );
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          className="flex h-10 min-w-0 items-center gap-2.5 rounded-md px-2 text-left transition-colors hover:bg-subtle"
          aria-label={t('shell.orgSwitcher.title')}
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary font-display text-sm font-bold text-on-primary">
            {(membership?.tenant.name ?? '?').slice(0, 1)}
          </span>
          <span className="hidden min-w-0 sm:block">
            <span className="block truncate text-sm font-bold text-text">
              {membership?.tenant.name ?? t('shell.orgSwitcher.none')}
            </span>
            <span className="block truncate text-xs text-muted">{membership?.orgUnit.name}</span>
          </span>
          <ChevronsUpDown size={15} className="text-muted" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className="glass z-50 w-72 rounded-lg p-1.5 elevated"
        >
          <p className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted">
            {t('shell.orgSwitcher.title')}
          </p>
          {memberships.map((m) => {
            const active = m.tenant.id === org?.tenantId && m.orgUnit.id === org?.orgUnitId;
            return (
              <Popover.Close asChild key={`${m.tenant.id}:${m.orgUnit.id}`}>
                <button
                  onClick={() => setOrg({ tenantId: m.tenant.id, orgUnitId: m.orgUnit.id })}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-subtle',
                    active && 'bg-subtle',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-semibold text-text">
                      {m.orgUnit.name}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {m.tenant.name} · {t(`roles.${m.role}`)}
                    </span>
                  </span>
                  {active && <Check size={16} className="text-primary" />}
                </button>
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
